import React from 'react';

/**
 * DashboardCharts - Compact charts optimized for dashboard quick overview
 * Professional, space-efficient charts for daily financial monitoring
 */

// Compact Expense Categories Bar Chart for Dashboard
export const QuickExpenseChart = ({ data, height = 180, formatCurrency }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'var(--gray-400)',
        fontSize: '0.875rem'
      }}>
        No expense data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map(item => item.value));
  const padding = { left: 15, right: 15, top: 20, bottom: 75 }; // More bottom space for labels
  const chartWidth = 400; // Wider for better spacing
  
  return (
    <div style={{ 
      width: '100%', 
      height: height, 
      overflow: 'visible',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '0.25rem'
    }}>
      <svg 
        width="100%" 
        height={height} 
        viewBox={`0 0 ${chartWidth} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ 
          width: '100%', 
          height: `${height}px`,
          display: 'block'
        }}
      >
        <defs>
          {/* Professional Red/Green Gradients */}
          <linearGradient id="expenseBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="1" />
            <stop offset="50%" stopColor="#dc2626" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#b91c1c" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="incomeBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="1" />
            <stop offset="50%" stopColor="#16a34a" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#15803d" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        
        {data.map((item, index) => {
          const availableWidth = chartWidth - padding.left - padding.right;
          const barHeight = (item.value / maxValue) * (height - padding.top - padding.bottom);
          const barWidth = Math.min(availableWidth / data.length * 0.75, 65); // Balanced width
          const spacing = availableWidth / data.length;
          const x = padding.left + (index * spacing) + (spacing - barWidth) / 2;
          const y = height - padding.bottom - barHeight;
          
          return (
            <g key={`bar-${index}`}>
              {/* Bar Shadow */}
              <rect
                x={x + 1}
                y={y + 1}
                width={barWidth}
                height={barHeight}
                fill="var(--gray-200)"
                rx="3"
                opacity="0.3"
              />
              
              {/* Main Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill="url(#expenseBarGradient)"
                rx="3"
                stroke="#dc2626"
                strokeWidth="1"
              />
              
              {/* Category Label - Rotated if needed */}
              <text
                x={x + barWidth / 2}
                y={height - padding.bottom + 16}
                textAnchor="middle"
                fontSize="11"
                fill="var(--black)"
                fontWeight="600"
                fontFamily="var(--font-primary)"
                transform={data.length > 4 ? `rotate(-25, ${x + barWidth / 2}, ${height - padding.bottom + 16})` : undefined}
              >
                {item.name.length > 8 ? item.name.substring(0, 8) + '...' : item.name}
              </text>
              
              {/* Compact Value Label */}
              <text
                x={x + barWidth / 2}
                y={height - padding.bottom + (data.length > 4 ? 45 : 32)}
                textAnchor="middle"
                fontSize="10"
                fill="var(--black)"
                fontWeight="600"
                fontFamily="var(--font-mono)"
              >
                {formatCurrency ? formatCurrency(item.value, { compact: true }) : item.value.toLocaleString()}
              </text>
              
              {/* Value on top of bar - always show for zoomed view */}
              {barHeight > 15 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 8}
                  textAnchor="middle"
                  fontSize="11"
                  fill="var(--black)"
                  fontWeight="700"
                  fontFamily="var(--font-mono)"
                >
                  {formatCurrency ? formatCurrency(item.value, { compact: true }) : item.value.toLocaleString()}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Weekly Balance Trend - Mini Line Chart for Dashboard
export const WeeklyBalanceTrend = ({ data, height = 120, formatCurrency }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'var(--gray-400)',
        fontSize: '0.875rem'
      }}>
        Add transactions to see trends
      </div>
    );
  }

  const width = 300;
  const padding = { left: 15, right: 15, top: 20, bottom: 30 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const values = data.map(d => d.balance || 0);
  const maxValue = Math.max(...values, 0);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;

  const getX = (index) => padding.left + (index * plotWidth) / Math.max(1, data.length - 1);
  const getY = (value) => padding.top + plotHeight - ((value - minValue) / range) * plotHeight;

  // Generate path
  const pathData = data.map((item, index) => {
    const x = getX(index);
    const y = getY(item.balance || 0);
    return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  const isPositiveTrend = data.length > 1 && (data[data.length - 1].balance || 0) > (data[0].balance || 0);

  return (
    <div style={{ width: '100%', height: height }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="balanceTrendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={isPositiveTrend ? "var(--income-green)" : "var(--expense-red)"} stopOpacity="0.3" />
            <stop offset="100%" stopColor={isPositiveTrend ? "var(--income-green)" : "var(--expense-red)"} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Zero line */}
        {minValue < 0 && maxValue > 0 && (
          <line
            x1={padding.left}
            y1={getY(0)}
            x2={padding.left + plotWidth}
            y2={getY(0)}
            stroke="var(--gray-300)"
            strokeWidth="1"
            strokeDasharray="2,2"
          />
        )}

        {/* Area fill */}
        <path
          d={`${pathData} L ${getX(data.length - 1)} ${padding.top + plotHeight} L ${padding.left} ${padding.top + plotHeight} Z`}
          fill="url(#balanceTrendGradient)"
        />

        {/* Trend line */}
        <path
          d={pathData}
          fill="none"
          stroke={isPositiveTrend ? "var(--income-green)" : "var(--expense-red)"}
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Data points */}
        {data.map((item, index) => (
          <circle
            key={`point-${index}`}
            cx={getX(index)}
            cy={getY(item.balance || 0)}
            r="3"
            fill={isPositiveTrend ? "var(--income-green)" : "var(--expense-red)"}
            stroke="white"
            strokeWidth="1.5"
          />
        ))}

        {/* Labels for first and last points */}
        <text
          x={padding.left}
          y={padding.top + plotHeight + 20}
          textAnchor="start"
          fontSize="9"
          fill="var(--gray-500)"
        >
          {data[0]?.period || 'Start'}
        </text>
        <text
          x={padding.left + plotWidth}
          y={padding.top + plotHeight + 20}
          textAnchor="end"
          fontSize="9"
          fill="var(--gray-500)"
        >
          {data[data.length - 1]?.period || 'Now'}
        </text>
      </svg>
    </div>
  );
};

// Budget Progress Ring - Circular Progress for Budget Status
export const BudgetProgressRing = ({ spent, budget, height = 120 }) => {
  const center = 60;
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const percentage = budget > 0 ? Math.min((spent / budget) * 100, 100) : 0;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const isOverBudget = spent > budget;
  const color = isOverBudget ? 'var(--expense-red)' : percentage > 80 ? 'var(--warning-orange)' : 'var(--income-green)';

  return (
    <div style={{ 
      width: '100%', 
      height: height, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <svg width={120} height={120} viewBox="0 0 120 120">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--gray-200)"
          strokeWidth="8"
        />
        
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${center} ${center})`}
          style={{
            transition: 'stroke-dashoffset 0.5s ease-in-out'
          }}
        />
        
        {/* Center text */}
        <text
          x={center}
          y={center - 5}
          textAnchor="middle"
          fontSize="16"
          fontWeight="600"
          fill={color}
        >
          {Math.round(percentage)}%
        </text>
        <text
          x={center}
          y={center + 12}
          textAnchor="middle"
          fontSize="10"
          fill="var(--gray-500)"
        >
          of budget
        </text>
      </svg>
    </div>
  );
};